"use client";

import { useEffect, useRef } from "react";

class Orb {
  x: number;
  y: number;
  radius: number;
  vx: number;
  vy: number;
  color: string;

  constructor(x: number, y: number, radius: number, vx: number, vy: number, color: string) {
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.vx = vx;
    this.vy = vy;
    this.color = color;
  }

  update(width: number, height: number) {
    this.x += this.vx;
    this.y += this.vy;

    // Bounce off edges
    if (this.x - this.radius < -this.radius || this.x + this.radius > width + this.radius) {
      this.vx *= -1;
    }
    if (this.y - this.radius < -this.radius || this.y + this.radius > height + this.radius) {
      this.vy *= -1;
    }
  }

  draw(ctx: CanvasRenderingContext2D) {
    const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius);
    gradient.addColorStop(0, this.color);
    gradient.addColorStop(1, "rgba(0, 0, 0, 0)"); // Fade to transparent

    ctx.beginPath();
    ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
  }
}

export function AnimatedBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let width = window.innerWidth;
    let height = window.innerHeight;

    const setCanvasSize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    setCanvasSize();

    // Create 6 large orbs with our cyber colors, EXTREME opacity and speed
    const orbs = [
      new Orb(
        width * 0.3, height * 0.3, Math.max(width, height) * 0.7,
        3.5, 2.5, "rgba(0, 243, 255, 0.9)" // Cyan
      ),
      new Orb(
        width * 0.7, height * 0.6, Math.max(width, height) * 0.6,
        -2.8, -3.2, "rgba(255, 0, 255, 0.85)" // Magenta
      ),
      new Orb(
        width * 0.5, height * 0.8, Math.max(width, height) * 0.65,
        3.2, -2.8, "rgba(0, 102, 255, 0.9)" // Blue
      ),
      new Orb(
        width * 0.2, height * 0.8, Math.max(width, height) * 0.5,
        -3.0, 2.0, "rgba(0, 243, 255, 0.8)" // Cyan 2
      ),
      new Orb(
        width * 0.8, height * 0.2, Math.max(width, height) * 0.55,
        2.5, -3.5, "rgba(255, 0, 255, 0.9)" // Magenta 2
      ),
      new Orb(
        width * 0.5, height * 0.4, Math.max(width, height) * 0.75,
        -2.0, 3.8, "rgba(0, 102, 255, 0.85)" // Blue 2
      ),
    ];

    const render = () => {
      // Clear canvas with a very slight dark trail effect (or just clear entirely)
      ctx.clearRect(0, 0, width, height);

      // We can use globalCompositeOperation for better blending
      ctx.globalCompositeOperation = "screen";

      orbs.forEach((orb) => {
        orb.update(width, height);
        orb.draw(ctx);
      });

      // Reset composite operation
      ctx.globalCompositeOperation = "source-over";

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    window.addEventListener("resize", setCanvasSize);

    return () => {
      window.removeEventListener("resize", setCanvasSize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="fixed inset-0 z-[-2] pointer-events-none"
        style={{ opacity: 1 }} // Full opacity for the canvas
      />
      {/* Grid pattern overlay - extremely light so the bright orbs shine through completely */}
      <div className="fixed inset-0 z-[-1] pointer-events-none bg-black/10 backdrop-blur-[1px] bg-grid-pattern opacity-30"></div>
    </>
  );
}
